import { logger } from '../services/logger.ts';
import { supabase } from '../lib/db.ts';
import { UserBet, MarketPool } from '../types.ts';

/**
 * Service for handling trade transaction operations
 */
export class BetTransactionService {
  /**
   * Place a bet on a market outcome
   * @param marketId The ID of the market
   * @param marketOptionId The ID of the market option to bet on
   * @param amount The amount to bet
   * @param serviceFeePercentage Service fee percentage (required)
   * @returns The created bet or null if there was an error
   */
  public static async placeBet(
    marketId: string,
    marketOptionId: number,
    amount: number,
    serviceFeePercentage: number
  ): Promise<UserBet | null> {
    // Track this API call for monitoring
    logger.trackRequest('BetTransaction:placeBet', {
      type: 'api_call',
      marketId,
      marketOptionId,
      amount,
      serviceFeePercentage,
    });

    try {
      logger.info(`===========================================================`);
      logger.info(`Attempting to place trade: market=${marketId}, optionId=${marketOptionId}, amount=${amount}, fee=${serviceFeePercentage}`);
      
      // Check if current user is an admin
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Authentication required to place a trade');
      }
      
      // Call the database function directly
      logger.info(`Calling place_bet database function`);
      const { data: response, error } = await supabase.rpc('place_bet', {
        p_market_id: marketId,
        p_market_option_id: marketOptionId,
        p_amount: amount,
        p_service_fee_percentage: serviceFeePercentage
      });
      logger.info(`Database function returned:`, response);

      if (error) {
        logger.error('Error placing trade:', error);
        logger.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      // Check if response is a string (bet ID) or an object (JSONB response)
      if (typeof response === 'string') {
        // If we got just a bet ID, fetch the full bet record
        const { data: bet, error: fetchError } = await supabase
          .from('user_bets')
          .select('*')
          .eq('id', response)
          .single();

        if (fetchError) {
          logger.error('Error fetching bet details:', fetchError);
          return null;
        }

        // Fetch the updated wallet balance after placing the bet
        try {
          const { data: walletData, error: walletError } = await supabase
            .from('user_wallets')
            .select('balance')
            .eq('user_id', user.id)
            .single();
            
          if (!walletError && walletData && typeof walletData.balance === 'number') {
            // Dispatch event with updated balance
            window.dispatchEvent(new CustomEvent('walletBalanceUpdated', {
              detail: { balance: walletData.balance }
            }));
            
            logger.info(`Dispatched wallet update after bet placement: ${walletData.balance}`);
          }
        } catch (walletError) {
          logger.error('Error fetching updated wallet balance after bet placement:', walletError);
          // Continue with the operation even if we can't update the balance
        }

        return bet;
      }

      // Handle JSONB response
      if (response && response.success === false) {
        logger.error('Error from database function:', response.error);
        throw new Error(response.error);
      }

      if (!response || !response.bet) {
        logger.error('No bet record returned from place_bet function', response);
        return null;
      }

      // Fetch the updated wallet balance after placing the bet via JSONB response
      try {
        const { data: walletData, error: walletError } = await supabase
          .from('user_wallets')
          .select('balance')
          .eq('user_id', user.id)
          .single();
          
        if (!walletError && walletData && typeof walletData.balance === 'number') {
          // Dispatch event with updated balance
          window.dispatchEvent(new CustomEvent('walletBalanceUpdated', {
            detail: { balance: walletData.balance }
          }));
          
          logger.info(`Dispatched wallet update after bet placement: ${walletData.balance}`);
        }
      } catch (walletError) {
        logger.error('Error fetching updated wallet balance after bet placement:', walletError);
        // Continue with the operation even if we can't update the balance
      }

      logger.info(`Trade placed successfully with ID: ${response.bet.id}`);
      return response.bet;
    } catch (error) {
      logger.error('Failed to place trade:', error);
      throw error; // Re-throw so the UI can display the error
    }
  }

  /**
   * Get all bets for the current user
   * @returns Array of user bets
   */
  public static async getUserBets(): Promise<UserBet[]> {
    try {
      const { data, error } = await supabase
        .from('user_bets')
        .select('*, markets(title, status, winning_outcome)')
        .order('time', { ascending: false });

      if (error) {
        logger.error('Error fetching user bets:', error.message);
        throw error;
      }

      return data || [];
    } catch (error) {
      logger.error('Failed to fetch user bets:', error);
      return [];
    }
  }

  /**
   * Get user bets for a specific market
   * @param marketId The ID of the market
   * @returns Array of user bets for the market
   */
  public static async getUserBetsForMarket(marketId: string): Promise<UserBet[]> {
    try {
      const { data, error } = await supabase
        .from('user_bets')
        .select('*')
        .eq('market_id', marketId)
        .order('time', { ascending: false });

      if (error) {
        logger.error(`Error fetching bets for market ${marketId}:`, error.message);
        throw error;
      }

      return data || [];
    } catch (error) {
      logger.error(`Failed to fetch bets for market ${marketId}:`, error);
      return [];
    }
  }

  /**
   * Get all bet-related transactions for the current user
   * @returns Array of bet transactions including payouts
   */
  public static async getBetTransactions() {
    try {
      const { data, error } = await supabase
        .from('bet_transactions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error fetching bet transactions:', error.message);
        throw error;
      }

      return data || [];
    } catch (error) {
      logger.error('Failed to fetch bet transactions:', error);
      return [];
    }
  }

  /**
   * Get market pools for a specific market
   * @param marketId The ID of the market
   * @returns Array of market pools
   */
  public static async getMarketPools(marketId: string): Promise<MarketPool[]> {
    try {
      const { data, error } = await supabase
        .from('market_pools')
        .select('*')
        .eq('market_id', marketId);

      if (error) {
        logger.error(`Error fetching pools for market ${marketId}:`, error.message);
        throw error;
      }

      return data || [];
    } catch (error) {
      logger.error(`Failed to fetch pools for market ${marketId}:`, error);
      return [];
    }
  }

  /**
   * Admin function to resolve a market
   * @param marketId The ID of the market
   * @param winningOutcome The name of the winning outcome
   * @returns True if market was resolved successfully, false otherwise
   */
  public static async resolveMarket(
    marketId: string,
    winningOutcome: string
  ): Promise<boolean> {
    try {
      logger.info(`Resolving market ${marketId} with winning outcome: ${winningOutcome}`);
      
      const params = {
        p_market_id: marketId,
        p_winning_outcome: winningOutcome,
      };
      
      logger.info('API Parameters:', params);
      
      const { data, error } = await supabase.rpc('resolve_market', params);

      if (error) {
        logger.error('Error resolving market:', error);
        logger.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      logger.info('Market resolution response:', data);
      return data || false;
    } catch (error) {
      logger.error('Failed to resolve market:', error);
      return false;
    }
  }

  /**
   * Admin function to payout all winning bets for a market
   * @param marketId The ID of the market
   * @returns The number of bets that were paid out
   */
  public static async payoutMarketWinners(
    marketId: string
  ): Promise<number> {
    try {
      logger.info(`Processing payouts for market ${marketId}`);
      
      // First, ensure all wallets are initialized for users with winning bets
      const { data: winningBets, error: betsError } = await supabase
        .from('user_bets')
        .select('user_id')
        .eq('market_id', marketId)
        .eq('status', 'pending')
        .eq('market_outcome_status', 'won');
      
      if (betsError) {
        logger.error('Error fetching winning bets:', betsError);
        throw betsError;
      }
      
      // Check and initialize wallets for users with winning bets
      if (winningBets && winningBets.length > 0) {
        const userIds = winningBets.map(bet => bet.user_id);
        
        // Get unique user IDs
        const uniqueUserIds = [...new Set(userIds)];
        
        for (const userId of uniqueUserIds) {
          // Check if user has a wallet and if it has a valid balance
          const { data: wallet, error: walletError } = await supabase
            .from('user_wallets')
            .select('*')
            .eq('user_id', userId)
            .single();
            
          if (walletError && walletError.code !== 'PGRST116') {
            // Some error other than "no rows returned" occurred
            logger.error(`Error checking wallet for user ${userId}:`, walletError);
            continue;
          }
          
          if (!wallet) {
            // Create wallet with zero balance if it doesn't exist
            const { error: createError } = await supabase
              .from('user_wallets')
              .insert({
                user_id: userId,
                balance: 0
              });
              
            if (createError) {
              logger.error(`Error creating wallet for user ${userId}:`, createError);
              continue;
            }
            
            logger.info(`Created wallet for user ${userId}`);
          } else if (wallet.balance === null) {
            // Update wallet with zero balance if it exists but has null balance
            const { error: updateError } = await supabase
              .from('user_wallets')
              .update({ balance: 0 })
              .eq('user_id', userId);
              
            if (updateError) {
              logger.error(`Error updating wallet balance for user ${userId}:`, updateError);
              continue;
            }
            
            logger.info(`Updated null balance to 0 for user ${userId}`);
          }
        }
      }
      
      // Now that wallets are initialized, call the RPC function
      const params = {
        p_market_id: marketId
      };
      
      logger.info('API Parameters:', params);
      
      const { data, error } = await supabase.rpc('payout_market_winners', params);

      if (error) {
        logger.error('Error processing payouts:', error);
        logger.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      logger.info('Payout response:', data);
      return data || 0;
    } catch (error) {
      logger.error('Failed to process payouts:', error);
      throw error; // Re-throw the error for the UI to handle
    }
  }

  /**
   * Admin function to payout a single winning bet
   * @param betId The ID of the bet to payout
   * @returns True if bet was paid out successfully
   */
  public static async payoutWinningBet(
    betId: string
  ): Promise<boolean> {
    try {
      logger.info(`Processing payout for bet ${betId}`);
      
      const params = {
        p_bet_id: betId
      };
      
      const { data, error } = await supabase.rpc('payout_winning_bet', params);

      if (error) {
        logger.error('Error processing bet payout:', error);
        logger.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      logger.info('Bet payout response:', data);
      return data || false;
    } catch (error) {
      logger.error('Failed to process bet payout:', error);
      throw error;
    }
  }

  /**
   * Cancel a bet and refund the amount to the user
   * @param betId The ID of the bet to cancel
   * @returns True if bet was cancelled successfully, false otherwise
   */
  public static async cancelBet(betId: string): Promise<boolean> {
    try {
      logger.info(`Cancelling bet with ID: ${betId}`);
      
      const { data, error } = await supabase.rpc('cancel_bet', {
        p_bet_id: betId
      });

      if (error) {
        logger.error('Error cancelling bet:', error);
        logger.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      logger.info('Bet cancellation response:', data);
      
      // If the cancellation was successful, update the UI
      if (data) {
        try {
          // Trigger bet history update to refresh market stats
          window.dispatchEvent(new CustomEvent('betHistoryUpdated'));
          
          // Get the current user
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            // Fetch their updated wallet balance
            const { data: walletData, error: walletError } = await supabase
              .from('user_wallets')
              .select('balance')
              .eq('user_id', user.id)
              .single();
              
            if (!walletError && walletData && typeof walletData.balance === 'number') {
              // Dispatch event with updated balance
              window.dispatchEvent(new CustomEvent('walletBalanceUpdated', {
                detail: { balance: walletData.balance }
              }));
              
              logger.info(`Dispatched wallet update after cancellation: ${walletData.balance}`);
            }
          }
        } catch (walletError) {
          logger.error('Error fetching updated wallet balance after bet cancellation:', walletError);
          // Continue with the operation even if we can't update the balance
        }
      }
      
      return data || false;
    } catch (error) {
      logger.error('Failed to cancel bet:', error);
      throw error;
    }
  }
} 